import React from 'react';
import {
  Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  Divider, Box, Typography, Avatar, IconButton, Collapse, useTheme
} from '@mui/material';
import {
  Dashboard, Inventory, AssignmentInd, Info, Logout,
  ChevronLeft, ChevronRight, ExpandLess, ExpandMore,
  AutoAwesomeMotion, LibraryBooks, Place, SupervisorAccount, Description,
  ViewWeek
} from '@mui/icons-material';
import { usePermissions } from '../hooks/usePermission';
import { useNavigate } from 'react-router-dom';

import imgLogo from '../assets/png/nopala_logo.png';
const drawerWidth = 290;

export default function SidebarDrawer({ open, handleDrawerClose }) {
  const theme = useTheme();
  const navigate = useNavigate();

  const { 
    user,
    canViewInventory, canViewPart, canViewProperty,
    canViewArea, canViewBuilding, canViewAsignSafe
  } = usePermissions();

  const [openCatalogs, setOpenCatalogs] = React.useState(false);
  const [openReports, setOpenReports] = React.useState(false);

  const handleNavigation = (path) => {
    navigate(path);
    if (window.innerWidth < 900) handleDrawerClose();
  }

  //CONSERVAR ANIMACION PARA CADA REDIRECCION
  const listItemButtonStyles = {
    borderRadius: 2, mx: 1, py: 1.5,
    transition: 'all 0.3s ease',
    '&:hover': {
      background: 'rgba(255,255,255,0.15)',
      transform: 'translateX(8px)',
      boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
    }
  }

  return (
    <Drawer
      variant="temporary"
      anchor="left"
      open={open}
      onClose={handleDrawerClose}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          pb: 2,
          width: drawerWidth,
          boxSizing: 'border-box',
          background: 'linear-gradient(180deg, #833c3cff 20%, #3b1e1eff 100%)',
          color: 'white',
          borderRight: 'none',
          boxShadow: '8px 0 30px rgba(0,0,0,0.4)',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          overflowX: 'hidden',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' }
        }
      }}
    >
      {/* ENCABEZADO DEL DRAWER */}
      <Box sx={{ p: 3, textAlign: 'center', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <IconButton
          onClick={handleDrawerClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'white',
            background: 'rgba(255,255,255,0.1)',
            '&:hover': { background: 'rgba(255,255,255,0.2)' }
          }}
        >
          {theme.direction === 'ltr' ? <ChevronLeft /> : <ChevronRight />}
        </IconButton>

        <Avatar
          src={imgLogo}
          alt="Logo"
          sx={{
            width: 70, height: 70, mx: 'auto', mb: 2,
            border: '4px solid rgba(255,255,255,0.2)',
            boxShadow: '0 8px 25px rgba(0,0,0,0.5)',
            backgroundColor: '#fff',
          }}
        />
        <Typography variant="h6" fontWeight="bold" sx={{ textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>
          H. AYUNTAMIENTO
        </Typography>
        <Typography variant="caption" sx={{ opacity: 0.8 }}>
          Nopala de Villagrán
        </Typography>
      </Box>

      {/* INFORMACIÓN DEL USUARIO */}
      <Box sx={{ p: 2, background: 'rgba(255,255,255,0.05)', textAlign: 'center' }}>
        <Avatar
          sx={{
            width: 60, height: 60, mx: 'auto', mb: 1,
            border: '3px solid white',
            boxShadow: '0 4px 15px rgba(0,0,0,0.4)',
          }}
        >
          {user?.username?.[0]?.toUpperCase() || 'U'}
        </Avatar>
        <Typography variant="subtitle2" fontWeight="bold">
          {user?.username || 'USER'}
        </Typography>
        <Typography variant="caption" sx={{ opacity: 0.9 }}>
          {user?.role || 'ROLE'}
        </Typography>
      </Box>

      <Divider sx={{ background: 'rgba(255,255,255,0.15)', my: 1 }} />

      {/* MENÚ PRINCIPAL */}
      <List sx={{ px: 1 }}>
        {canViewInventory && (
          <ListItem disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              sx={listItemButtonStyles}
              onClick={() => {
                handleDrawerClose();
                handleNavigation('/inventory')
              }}
            >
              <ListItemIcon sx={{ color: '#e0e7ff', minWidth: 45 }}>
                <Dashboard />
              </ListItemIcon>
              <ListItemText primary="INVENTARIO DE BIENES" />
            </ListItemButton>
          </ListItem>
        )}

        {/* MENU DE CATALOGOS */}
        <ListItemButton
          sx={listItemButtonStyles}
          onClick={() => setOpenCatalogs(!openCatalogs)}
        >
          <ListItemIcon sx={{ color: '#e0e7ff' }}>
            <ViewWeek />
          </ListItemIcon>
          <ListItemText primary="CATÁLOGOS" />
          {openCatalogs ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>

        <Collapse in={openCatalogs} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {canViewPart && (
              <ListItemButton
                sx={{ ...listItemButtonStyles, pl: 4 }}
                onClick={() => {
                  handleDrawerClose();
                  handleNavigation('/part');
                }}
              >
                <ListItemIcon sx={{ color: '#e0e7ff' }}><AutoAwesomeMotion /></ListItemIcon>
                <ListItemText primary="PARTIDAS" />
              </ListItemButton>
            )}
            {canViewProperty && (
              <ListItemButton
                sx={{ ...listItemButtonStyles, pl: 4 }}
                onClick={() => {
                  handleDrawerClose();
                  handleNavigation('/property');
                }}
              >
                <ListItemIcon sx={{ color: '#e0e7ff' }}><LibraryBooks /></ListItemIcon>
                <ListItemText primary="BIENES" />
              </ListItemButton>
            )}
            {canViewArea && (
              <ListItemButton
                sx={{ ...listItemButtonStyles, pl: 4 }}
                onClick={() => {
                  handleDrawerClose();
                  handleNavigation('/area');
                }}
              >
                <ListItemIcon sx={{ color: '#e0e7ff' }}><SupervisorAccount /></ListItemIcon>
                <ListItemText primary="ÁREAS" />
              </ListItemButton>
            )}
            {canViewBuilding && (
              <ListItemButton
                sx={{ ...listItemButtonStyles, pl: 4 }}
                onClick={() => {
                  handleDrawerClose();
                  handleNavigation('/building');
                }}
              >
                <ListItemIcon sx={{ color: '#e0e7ff' }}><Place /></ListItemIcon>
                <ListItemText primary="EDIFICIOS" />
              </ListItemButton>
            )}
          </List>
        </Collapse>

        <Divider sx={{ background: 'rgba(255,255,255,0.1)', my: 1 }} />

        {/*  MENU DE REPORTES */}
        <ListItemButton
          sx={listItemButtonStyles}
          onClick={() => setOpenReports(!openReports)}
        >
          <ListItemIcon sx={{ color: '#e0e7ff' }}>
            <Description />
          </ListItemIcon>
          <ListItemText primary="REPORTES" />
          {openReports ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>

        <Collapse in={openReports} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {canViewAsignSafe && (
              <ListItemButton
                sx={{ ...listItemButtonStyles, pl: 4 }}
                onClick={() => {
                  handleDrawerClose();
                  handleNavigation('/asign-safe');
                }}
              >
                <ListItemIcon sx={{ color: '#e0e7ff' }}><AssignmentInd /></ListItemIcon>
                <ListItemText primary="ASIGNACIÓN DE RESGUARDOS" />
              </ListItemButton>
            )}
          </List>
        </Collapse>

        {/* {canViewInfo && (
          <ListItemButton
            sx={listItemButtonStyles}
            onClick={() => handleNavigation('/info')}
          >
            <ListItemIcon sx={{ color: '#e0e7ff' }}>
              <Info />
            </ListItemIcon>
            <ListItemText primary="Mi Información" />
          </ListItemButton>
        )} */}
      </List>
      {/* <Box sx={{ p: 2, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <ListItem disablePadding>
          <ListItemButton
            sx={{
              ...listItemButtonStyles,
              background: 'rgba(239, 68, 68, 0.2)',
              '&:hover': { background: 'rgba(239, 68, 68, 0.35)', transform: 'translateX(4px)' },
            }}
            onClick={logout}
          >
            <ListItemIcon sx={{ color: '#fca5a5', minWidth: 45 }}>
              <Logout />
            </ListItemIcon>
            <ListItemText primary="Cerrar Sesión" />
          </ListItemButton>
        </ListItem>
      </Box> */}
    </Drawer>
  );
}
